import { SEO } from "@/components/SEO";
import { Helmet } from "react-helmet-async";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { NavLink } from "@/components/NavLink";
import { 
  Search,
  Check, 
  Phone,
  Calendar,
  ArrowRight,
  TrendingUp,
  MapPin,
  FileText,
  Link
} from "lucide-react";

const CANONICAL = "https://www.leafrushmarketing.com/lawn-care-website-seo-services/";

const faqItems = [
  {
    question: "What are lawn care website SEO services?",
    answer: "SEO services for lawn care websites include on-page optimization (titles, headings, content), service page structure, service area pages, internal linking, schema markup, and Google Business Profile alignment—all designed to help you rank higher in local searches."
  },
  {
    question: "How is this different from the SEO guide page?",
    answer: "Our SEO guide teaches you what to do. This page is about what we do for you. We implement on-page SEO, build proper page structures, and optimize your site so it ranks—you don't have to learn the technical details."
  },
  {
    question: "Do I need SEO if I'm running Google Ads?",
    answer: "Yes. SEO drives organic traffic that doesn't cost per click. Many lawn care companies use both: ads for immediate leads, SEO for long-term, sustainable traffic. We can set up your site for both."
  },
  {
    question: "What's included in your SEO setup?",
    answer: "Every website we build includes: proper title tags and meta descriptions, heading structure (H1, H2, etc.), internal linking between pages, schema markup for local business, fast mobile-first design, and service/service area pages optimized for local keywords."
  },
  {
    question: "Do you do ongoing SEO or just initial setup?",
    answer: "Our subscription includes a solid SEO foundation built into your site. For ongoing SEO (link building, content creation, monthly optimization), we offer add-on services. Most lawn care businesses see strong results from our initial setup alone."
  },
  {
    question: "How long until I see SEO results?",
    answer: "Local SEO typically shows movement in 2–4 months, with stronger results at 6–12 months. New sites take longer than established domains. Our structure gives you the best possible starting point."
  },
  {
    question: "What are service area pages and why do I need them?",
    answer: "Service area pages are location-specific pages (e.g., 'Lawn Care in Springfield') that help you rank for local searches. Each city or town you serve should have its own page to capture local traffic."
  },
  {
    question: "What's the difference between on-page SEO and off-page SEO?",
    answer: "On-page SEO is what we control on your website: content, structure, titles, internal links. Off-page SEO is external: backlinks, citations, reviews. We focus on on-page—it's the foundation. GBP optimization helps with local signals."
  },
  {
    question: "Do you guarantee first-page rankings?",
    answer: "No one can guarantee rankings—Google controls the algorithm. What we guarantee: a properly structured, SEO-ready site that gives you the best chance to rank. Combined with good service and reviews, most clients see significant improvement."
  },
  {
    question: "How does website SEO connect to Google Business Profile?",
    answer: "Your website and GBP work together. Consistent NAP (name, address, phone), matching service categories, and local content on your site strengthen your GBP rankings. We can help with GBP optimization as an add-on."
  }
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": `${CANONICAL}#faq`,
  "mainEntity": faqItems.map(item => ({
    "@type": "Question",
    "name": item.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": item.answer
    }
  }))
};

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${CANONICAL}#webpage`,
  "url": CANONICAL,
  "name": "Lawn Care Website SEO Services | Leaf Rush Marketing",
  "description": "Professional SEO services for lawn care websites. On-page optimization, service pages, service area pages, and local SEO structure that helps you rank.",
  "isPartOf": {
    "@id": "https://www.leafrushmarketing.com/#website"
  },
  "about": {
    "@id": "https://www.leafrushmarketing.com/#organization"
  }
};

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": "https://www.leafrushmarketing.com/#organization",
  "name": "Leaf Rush Marketing",
  "url": "https://www.leafrushmarketing.com",
  "logo": "https://www.leafrushmarketing.com/og-image.jpg"
};

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${CANONICAL}#service`,
  "name": "Lawn Care Website SEO Services",
  "description": "SEO services specifically for lawn care websites. Includes on-page optimization, service page structure, service area pages, internal linking, schema markup, and local SEO setup.",
  "provider": {
    "@id": "https://www.leafrushmarketing.com/#organization"
  },
  "areaServed": {
    "@type": "Country",
    "name": "United States"
  },
  "serviceType": "SEO Services"
};

const LawnCareWebsiteSEOServices = () => {
  return (
    <>
      <SEO 
        title="Lawn Care Website SEO Services: Rank Higher Locally"
        description="Professional SEO services for lawn care websites. On-page optimization, service pages, service area pages, and local SEO structure that helps you rank."
        canonical={CANONICAL}
        ogUrl={CANONICAL}
        ogImage="https://www.leafrushmarketing.com/og-image.jpg"
        keywords="lawn care website SEO services, on-page SEO for lawn care websites, local SEO structure for lawn care websites, rank lawn care service pages"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      </Helmet>

      <div className="pt-24">
        {/* Hero */}
        <section className="py-12 md:py-16 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <Badge className="mb-4">Website SEO Services</Badge>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              Lawn Care Website SEO Services: Rank Higher in Local Searches
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mb-4">
              We're not a lead generation company. Our lawn care website SEO is built into the website design—we structure your <NavLink to="/lawn-care-website-design/" className="text-primary hover:underline">lawn care web design</NavLink> with on-page SEO that helps your service pages rank.
            </p>
            <p className="text-muted-foreground">
              Every lawn care website design we build includes proper structure, optimized service pages, and local SEO signals. SEO is part of the website, not a separate service.
            </p>
          </div>
        </section>

        {/* Quick Answer Box */}
        <section className="py-12 bg-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <Card className="border-2 border-primary/20">
              <CardContent className="p-6 md:p-8">
                <div className="flex items-center gap-2 mb-6">
                  <Search className="h-6 w-6 text-primary" />
                  <h2 className="text-2xl font-bold">Quick Answer: What SEO Do We Provide?</h2>
                </div>
                
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                    <span><strong>On-page optimization</strong> — Titles, headings, meta descriptions done right</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                    <span><strong>Service pages</strong> — Dedicated pages for each service you offer</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                    <span><strong>Service area pages</strong> — Location pages for each city you serve</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                    <span><strong>Technical SEO</strong> — Fast loading, mobile-first, schema markup</span>
                  </li>
                </ul>

                <div className="flex flex-col sm:flex-row gap-3">
                  <a href="#get-started">
                    <Button size="lg" className="w-full sm:w-auto">
                      <Phone className="mr-2 h-4 w-4" />
                      Get Started
                    </Button>
                  </a>
                  <NavLink to="/websites/">
                    <Button size="lg" variant="outline" className="w-full sm:w-auto">
                      See Website Pricing
                    </Button>
                  </NavLink>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* What We Do */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">What Our SEO Services Include</h2>
            <p className="text-muted-foreground mb-8">
              Every website we build comes with SEO baked in—not as an afterthought, but as the foundation:
            </p>

            <div className="space-y-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <FileText className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">On-Page Optimization</h3>
                      <p className="text-muted-foreground">
                        Proper title tags, meta descriptions, H1/H2 headings, and content structure on every page. The basics that many sites get wrong.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <TrendingUp className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Dedicated Service Pages</h3>
                      <p className="text-muted-foreground">
                        Each service (mowing, fertilization, aeration, etc.) gets its own <NavLink to="/service-pages-that-rank/" className="text-primary hover:underline">optimized page</NavLink>—not just a list on one generic page.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <MapPin className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Service Area Pages</h3>
                      <p className="text-muted-foreground">
                        Location-specific pages that rank for "lawn care in [your city]." Each area you serve gets its own <NavLink to="/service-area-pages-local-seo/" className="text-primary hover:underline">SEO-optimized page</NavLink>.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Link className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Internal Linking Structure</h3>
                      <p className="text-muted-foreground">
                        Pages link to each other logically—helping Google understand your site and passing SEO value between pages.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Search className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Schema Markup</h3>
                      <p className="text-muted-foreground">
                        Local business schema, FAQ schema, and service schema—structured data that helps Google understand and feature your content.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* SEO + GBP */}
        <section className="py-12 bg-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">Website SEO + Google Business Profile = Local Dominance</h2>
            
            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-muted/50 rounded-lg border">
                <ArrowRight className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <h3 className="font-semibold">Consistent Information</h3>
                  <p className="text-sm text-muted-foreground">
                    Your website and GBP show the same name, address, phone, and services. Consistency builds trust with Google.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-muted/50 rounded-lg border">
                <ArrowRight className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <h3 className="font-semibold">Website Backs Up GBP</h3>
                  <p className="text-sm text-muted-foreground">
                    When Google sees your GBP linked to a professional, optimized website, it reinforces your legitimacy and boosts rankings.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-muted/50 rounded-lg border">
                <ArrowRight className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <h3 className="font-semibold">Local Content Signals</h3>
                  <p className="text-sm text-muted-foreground">
                    Service area pages on your website strengthen the local signals Google uses to rank your business in map results.
                  </p>
                </div>
              </div>
            </div>

            <p className="text-sm text-muted-foreground mt-6">
              Need help with GBP? See our <NavLink to="/growth-add-ons/gbp/" className="text-primary hover:underline">Google Business Profile optimization</NavLink> add-on.
            </p>
          </div>
        </section>

        {/* SEO vs Ads */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">Should I Do SEO or Google Ads?</h2>
            <p className="text-muted-foreground mb-6">
              Not either/or—most successful lawn care companies do both:
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg mb-4">Website SEO</h3>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <span>Free organic traffic (no cost per click)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <span>Builds long-term, sustainable lead flow</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <span>Results compound over time</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <span>Takes 3–6+ months to see strong results</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg mb-4">Google Ads</h3>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <span>Immediate visibility and leads</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <span>Pay per click (cost = volume)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <span>Easy to scale up or pause</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <span>Stops when budget stops</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <p className="text-sm text-muted-foreground mt-6 text-center">
              We offer both. See our <NavLink to="/growth-add-ons/google-ads/" className="text-primary hover:underline">Google Ads management</NavLink> service.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 bg-primary/5">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Ready for a Website Built for SEO?</h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              Book a 15-minute call. We'll discuss your lawn care business, your service areas, and how we build sites that rank.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="#get-started">
                <Button size="lg">
                  <Calendar className="mr-2 h-4 w-4" />
                  Get Started
                </Button>
              </a>
              <NavLink to="/lawn-care-website-design/">
                <Button size="lg" variant="outline">
                  See What We Build
                </Button>
              </NavLink>
            </div>
          </div>
        </section>

        {/* Related Links */}
        <section className="py-12 bg-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl font-bold mb-6">Related Resources</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <NavLink to="/lawn-care-website-design/" className="block p-4 border rounded-lg hover:border-primary transition-colors">
                <h3 className="font-semibold mb-1">Lawn Care Website Design</h3>
                <p className="text-sm text-muted-foreground">What we build and how it converts</p>
              </NavLink>
              <NavLink to="/lawn-care-website-seo/" className="block p-4 border rounded-lg hover:border-primary transition-colors">
                <h3 className="font-semibold mb-1">Lawn Care Website SEO Guide</h3>
                <p className="text-sm text-muted-foreground">Educational guide on SEO best practices</p>
              </NavLink>
              <NavLink to="/service-pages-that-rank/" className="block p-4 border rounded-lg hover:border-primary transition-colors">
                <h3 className="font-semibold mb-1">Service Pages That Rank</h3>
                <p className="text-sm text-muted-foreground">How to structure service pages for SEO</p>
              </NavLink>
              <NavLink to="/resources/" className="block p-4 border rounded-lg hover:border-primary transition-colors">
                <h3 className="font-semibold mb-1">Free Resources</h3>
                <p className="text-sm text-muted-foreground">Guides and checklists for lawn care businesses</p>
              </NavLink>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-8">Lawn Care Website SEO Services FAQ</h2>
            <Accordion type="single" collapsible className="w-full">
              {faqItems.map((item, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left">{item.question}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{item.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Calendly */}
        <section id="schedule-call" className="py-12 bg-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">Book Your 15-Minute Fit Call</h2>
              <p className="text-muted-foreground">
                We'll discuss your lawn care business, your SEO goals, and how we build sites that rank.
              </p>
            </div>
            <CalendlyEmbed />
          </div>
        </section>
      </div>
    </>
  );
};

export default LawnCareWebsiteSEOServices;
